import { DriverRankSettingsActivationAction, DriverRankSettingsService } from '@/backend/market'
import type { DriverRankSettingEntity } from '../../domain/entities/DriverRankSettingEntity'
import { DriverRankSettingRepository } from '../../domain/repositories/DriverRankSettingRepository'
import { DriverRankSettingModel } from '../models/DriverRankSettingModel'

export class DriverRankSettingRepositoryImpl implements DriverRankSettingRepository {
  async getDriverRankSettings(): Promise<DriverRankSettingEntity[] | { error: true }> {
    try {
      const response = await DriverRankSettingsService.getDriverRankSettingsAdmin()
      return response.map((item) => new DriverRankSettingModel(item))
    } catch {
      return { error: true }
    }
  }

  async createDriverRankSetting(
    data: DriverRankSettingEntity
  ): Promise<DriverRankSettingEntity | { error: true }> {
    try {
      const response = await DriverRankSettingsService.postDriverRankSettingsAdmin({
        requestBody: {
          acceptanceWeight: data.acceptanceWeight ?? 0,
          adherenceWeight: data.adherenceWeight ?? 0,
          effectiveDaysWeight: data.effectiveDaysWeight ?? 0,
          dailyHoursWeight: data.dailyHoursWeight ?? 0,
          productivityWeight: data.productivityWeight ?? 0,
          minorPenaltyScoreReduction: data.minorPenaltyScoreReduction ?? 0,
          majorPenaltyScoreReduction: data.majorPenaltyScoreReduction ?? 0,
          criticalPenaltyScoreReduction: data.criticalPenaltyScoreReduction ?? 0,
          dailyHoursBaseHours: data.dailyHoursBaseHours ?? 0,
          dailyHoursBaseScore: data.dailyHoursBaseScore ?? 0,
          dailyHoursExtraPerHourScore: data.dailyHoursExtraPerHourScore ?? 0,
          dailyHoursMaxExtraScore: data.dailyHoursMaxExtraScore ?? 0,
          effectiveDayMinAdherenceThreshold: data.effectiveDayMinAdherenceThreshold ?? 0,
          rankingWindowDays: data.rankingWindowDays ?? 0,
          productivityOrdersWeight: data.productivityOrdersWeight ?? 0,
          productivityUtilizationWeight: data.productivityUtilizationWeight ?? 0,
          dailyProductivityMinOnlineMinutes: data.dailyProductivityMinOnlineMinutes ?? 0,
          weeklyProductivityMinOnlineMinutes: data.weeklyProductivityMinOnlineMinutes ?? 0,
          productivityOrdersSmoothingAlpha: data.productivityOrdersSmoothingAlpha ?? 0,
          normalizationMinClamp: data.normalizationMinClamp ?? 0,
          normalizationMaxClamp: data.normalizationMaxClamp ?? 0,
          // Omitted when blank so the backend applies its own default (240).
          // Never falls back to 0 like the fields above: 0 is out of the accepted 1-1440 range.
          minPartialDayOffRemainderMinutes: data.minPartialDayOffRemainderMinutes ?? undefined
        }
      })
      return new DriverRankSettingModel(response)
    } catch {
      return { error: true }
    }
  }

  async updateDriverRankSetting(
    id: string,
    data: DriverRankSettingEntity
  ): Promise<DriverRankSettingEntity | { error: true }> {
    try {
      const response = await DriverRankSettingsService.putDriverRankSettingsAdmin({
        id,
        requestBody: {
          acceptanceWeight: data.acceptanceWeight ?? 0,
          adherenceWeight: data.adherenceWeight ?? 0,
          effectiveDaysWeight: data.effectiveDaysWeight ?? 0,
          dailyHoursWeight: data.dailyHoursWeight ?? 0,
          productivityWeight: data.productivityWeight ?? 0,
          minorPenaltyScoreReduction: data.minorPenaltyScoreReduction ?? 0,
          majorPenaltyScoreReduction: data.majorPenaltyScoreReduction ?? 0,
          criticalPenaltyScoreReduction: data.criticalPenaltyScoreReduction ?? 0,
          dailyHoursBaseHours: data.dailyHoursBaseHours ?? 0,
          dailyHoursBaseScore: data.dailyHoursBaseScore ?? 0,
          dailyHoursExtraPerHourScore: data.dailyHoursExtraPerHourScore ?? 0,
          dailyHoursMaxExtraScore: data.dailyHoursMaxExtraScore ?? 0,
          effectiveDayMinAdherenceThreshold: data.effectiveDayMinAdherenceThreshold ?? 0,
          rankingWindowDays: data.rankingWindowDays ?? 0,
          productivityOrdersWeight: data.productivityOrdersWeight ?? 0,
          productivityUtilizationWeight: data.productivityUtilizationWeight ?? 0,
          dailyProductivityMinOnlineMinutes: data.dailyProductivityMinOnlineMinutes ?? 0,
          weeklyProductivityMinOnlineMinutes: data.weeklyProductivityMinOnlineMinutes ?? 0,
          productivityOrdersSmoothingAlpha: data.productivityOrdersSmoothingAlpha ?? 0,
          normalizationMinClamp: data.normalizationMinClamp ?? 0,
          normalizationMaxClamp: data.normalizationMaxClamp ?? 0,
          // Always round-tripped from the GET. The backend treats an absent value as
          // "keep the stored one", so omitting it would silently discard an admin's edit.
          minPartialDayOffRemainderMinutes: data.minPartialDayOffRemainderMinutes ?? undefined
        }
      })
      return new DriverRankSettingModel(response)
    } catch {
      return { error: true }
    }
  }

  async activateDriverRankSetting(
    id: string,
    action: DriverRankSettingsActivationAction
  ): Promise<DriverRankSettingEntity | { error: true }> {
    try {
      const response = await DriverRankSettingsService.postDriverRankSettingsActivationAdmin({
        id,
        requestBody: {
          action
        }
      })
      return new DriverRankSettingModel(response)
    } catch {
      return { error: true }
    }
  }
}
